import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Grid as MuiGrid,
  useMediaQuery,
  Tooltip,
  useTheme,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { getAllJobs, deleteJob, getAllVolunteers, getAllRegistrations, updateRegistration, createRegistration, getTimeslotsByJobId, registerVolunteer } from '../services/api';
import { Job, Volunteer, Registration, JobWithRegistrations, Timeslot } from '../models/types';
import { SelectChangeEvent } from '@mui/material';

// Extended interface for timeslots with registration info
interface TimeslotWithRegistrationInfo extends Timeslot {
  registeredVolunteers: number;
  spotsLeft: number;
  isVolunteerAlreadyRegistered: boolean;
  hasTimeConflict: boolean;
}

// Function to fix Grid component props issue
const Grid = (props: any) => <MuiGrid {...props} />;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [registrationsData, setRegistrationsData] = useState<JobWithRegistrations[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [updatingRegistrationId, setUpdatingRegistrationId] = useState<string | null>(null);
  const [volunteerExistingTimeslots, setVolunteerExistingTimeslots] = useState<{ start: string, end: string }[]>([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [volunteerSearchTerm, setVolunteerSearchTerm] = useState('');
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  // Sorting state for volunteers table
  const [volunteerSort, setVolunteerSort] = useState<'name-asc' | 'name-desc' | 'reg-asc' | 'reg-desc' | 'time-asc' | 'time-desc'>('name-asc');

  // Helper: count registrations for a volunteer
  const getVolunteerRegistrationCount = React.useCallback((volunteerId: string): number => {
    if (!registrationsData || registrationsData.length === 0) return 0;
    let count = 0;
    registrationsData.forEach(jobData => {
      if (jobData.registrations && jobData.registrations.length > 0) {
        count += jobData.registrations.filter(r => r.volunteerId === volunteerId).length;
      }
    });
    return count;
  }, [registrationsData]);

  // Helper: get total registered minutes for a volunteer
  const getVolunteerRegisteredMinutes = React.useCallback((volunteerId: string): number => {
    if (!registrationsData || registrationsData.length === 0) return 0;
    let total = 0;
    registrationsData.forEach(jobData => {
      if (jobData.registrations && jobData.registrations.length > 0) {
        jobData.registrations.forEach(r => {
          if (r.volunteerId === volunteerId && r.timeslotStart && r.timeslotEnd) {
            const start = new Date(r.timeslotStart).getTime();
            const end = new Date(r.timeslotEnd).getTime();
            if (!isNaN(start) && !isNaN(end) && end > start) {
              total += Math.round((end - start) / 60000);
            }
          }
        });
      }
    });
    return total;
  }, [registrationsData]);

  // Memoized sorted volunteers
  const sortedFilteredVolunteers = React.useMemo(() => {
    const arr = [...filteredVolunteers];
    arr.sort((a, b) => {
      if (volunteerSort === 'name-asc') {
        return a.name.localeCompare(b.name);
      } else if (volunteerSort === 'name-desc') {
        return b.name.localeCompare(a.name);
      } else if (volunteerSort === 'reg-asc') {
        return getVolunteerRegistrationCount(a.id) - getVolunteerRegistrationCount(b.id);
      } else if (volunteerSort === 'reg-desc') {
        return getVolunteerRegistrationCount(b.id) - getVolunteerRegistrationCount(a.id);
      } else if (volunteerSort === 'time-asc') {
        return getVolunteerRegisteredMinutes(a.id) - getVolunteerRegisteredMinutes(b.id);
      } else if (volunteerSort === 'time-desc') {
        return getVolunteerRegisteredMinutes(b.id) - getVolunteerRegisteredMinutes(a.id);
      }
      return 0;
    });
    return arr;
  }, [filteredVolunteers, volunteerSort, getVolunteerRegistrationCount, getVolunteerRegisteredMinutes]);

  const [newVolunteerDialogOpen, setNewVolunteerDialogOpen] = useState(false);  const [newVolunteerForm, setNewVolunteerForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    commodity: '',
  });
  const [volunteerFormErrors, setVolunteerFormErrors] = useState<{ [key: string]: string }>({});
  const [creatingVolunteer, setCreatingVolunteer] = useState(false);

  const [newRegistrationDialogOpen, setNewRegistrationDialogOpen] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>('');
  const [availableTimeslots, setAvailableTimeslots] = useState<TimeslotWithRegistrationInfo[]>([]);
  const [registrationFormErrors, setRegistrationFormErrors] = useState<{ [key: string]: string }>({});
  const [creatingRegistration, setCreatingRegistration] = useState(false);
  const [loadingTimeslots, setLoadingTimeslots] = useState(false);
  const [timeslotError, setTimeslotError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (tabValue === 0) {
          const data = await getAllJobs();
          setJobs(data);
        } else if (tabValue === 1) {
          const [volunteersData, registrationsDataRaw] = await Promise.all([
            getAllVolunteers(),
            getAllRegistrations()
          ]);
          setVolunteers(volunteersData);
          setRegistrationsData(registrationsDataRaw);
        } else if (tabValue === 2) {
          const data = await getAllRegistrations();

          const allRegistrations: Registration[] = [];
          data.forEach(jobData => {
            jobData.registrations.forEach(reg => {
              allRegistrations.push({
                ...reg,
                jobName: jobData.job.name
              });
            });
          });
          setFilteredRegistrations(allRegistrations);
          setRegistrationsData(data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue]);

  useEffect(() => {
    if (tabValue !== 2 || registrationsData.length === 0) return;

    const allRegistrations: Registration[] = [];
    registrationsData.forEach(jobData => {
      jobData.registrations.forEach(reg => {
        allRegistrations.push({
          ...reg,
          jobName: jobData.job.name
        });
      });
    });

    const allDates = new Set<string>();
    allRegistrations.forEach(reg => {
      if (reg.timeslotStart) {
        const date = new Date(reg.timeslotStart).toLocaleDateString();
        allDates.add(date);
      }
    });
    setAvailableDays(Array.from(allDates).sort());

    let filtered = allRegistrations;

    if (statusFilter !== 'all') {
      if (statusFilter === 'confirmed') {
        filtered = filtered.filter(reg => reg.isConfirmed && !reg.hasAttended);
      } else if (statusFilter === 'pending') {
        filtered = filtered.filter(reg => !reg.isConfirmed);
      } else if (statusFilter === 'attended') {
        filtered = filtered.filter(reg => reg.hasAttended);
      }
    }

    if (jobFilter !== 'all') {
      filtered = filtered.filter(reg => reg.jobId === jobFilter);
    }

    if (dayFilter !== 'all') {
      filtered = filtered.filter(reg => {
        if (reg.timeslotStart) {
          const regDate = new Date(reg.timeslotStart).toLocaleDateString();
          return regDate === dayFilter;
        }
        return false;
      });
    }

    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(reg =>
        (reg.volunteerName?.toLowerCase().includes(lowercaseSearch)) ||
        (reg.jobName?.toLowerCase().includes(lowercaseSearch))
      );
    }

    setFilteredRegistrations(filtered);
  }, [searchTerm, statusFilter, jobFilter, dayFilter, registrationsData, tabValue]);

  useEffect(() => {
    if (selectedVolunteer) {
      const existingTimeslots: { start: string, end: string }[] = [];

      registrationsData.forEach(jobData => {
        jobData.registrations.forEach(reg => {
          if (reg.volunteerId === selectedVolunteer && reg.timeslotStart && reg.timeslotEnd) {
            existingTimeslots.push({
              start: reg.timeslotStart,
              end: reg.timeslotEnd
            });
          }
        });
      });

      setVolunteerExistingTimeslots(existingTimeslots);
    } else {
      setVolunteerExistingTimeslots([]);
    }
  }, [selectedVolunteer, registrationsData]);

  useEffect(() => {
    if (selectedJob) {
      const fetchTimeslots = async () => {
        try {
          setLoadingTimeslots(true);
          setTimeslotError(null);
          const timeslots = await getTimeslotsByJobId(selectedJob);

          const timeslotsWithRegistrationCount: TimeslotWithRegistrationInfo[] = timeslots.map(timeslot => {
            const jobData = registrationsData.find(data => data.job.id === selectedJob);

            let registeredVolunteers = 0;
            let isVolunteerAlreadyRegistered = false;

            if (jobData) {
              registeredVolunteers = jobData.registrations.filter(
                reg => reg.timeslotId === timeslot.id
              ).length;

              if (selectedVolunteer) {
                isVolunteerAlreadyRegistered = jobData.registrations.some(
                  reg => reg.timeslotId === timeslot.id && reg.volunteerId === selectedVolunteer
                );
              }
            }

            let hasTimeConflict = false;
            if (selectedVolunteer && volunteerExistingTimeslots.length > 0) {
              const timeslotStart = new Date(timeslot.startTime).getTime();
              const timeslotEnd = new Date(timeslot.endTime).getTime();

              hasTimeConflict = volunteerExistingTimeslots.some(existing => {
                const existingStart = new Date(existing.start).getTime();
                const existingEnd = new Date(existing.end).getTime();

                return (
                  (timeslotStart >= existingStart && timeslotStart < existingEnd) || // Start during existing slot
                  (timeslotEnd > existingStart && timeslotEnd <= existingEnd) || // End during existing slot
                  (timeslotStart <= existingStart && timeslotEnd >= existingEnd) // Completely covering existing slot
                );
              });
            }

            return {
              ...timeslot,
              registeredVolunteers,
              spotsLeft: timeslot.requiredVolunteers - registeredVolunteers,
              isVolunteerAlreadyRegistered,
              hasTimeConflict
            };
          });

          const sortedTimeslots = [...timeslotsWithRegistrationCount].sort((a, b) => {
            if (a.spotsLeft > 0 && b.spotsLeft <= 0) {
              return -1;
            }
            if (a.spotsLeft <= 0 && b.spotsLeft > 0) {
              return 1;
            }
            return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          });

          setAvailableTimeslots(sortedTimeslots);
        } catch (error) {
          console.error('Error fetching timeslots:', error);
          setTimeslotError('Zeitfenster für diesen Dienst konnten nicht geladen werden');
          setAvailableTimeslots([]);
        } finally {
          setLoadingTimeslots(false);
        }
      };

      fetchTimeslots();
    } else {
      setAvailableTimeslots([]);
    }
  }, [selectedJob, registrationsData, selectedVolunteer, volunteerExistingTimeslots]);

  useEffect(() => {
    if (!volunteers.length) {
      setFilteredVolunteers([]);
      return;
    }

    if (!volunteerSearchTerm) {
      setFilteredVolunteers(volunteers);
      return;
    }    const lowercaseSearch = volunteerSearchTerm.toLowerCase();
    const filtered = volunteers.filter(volunteer => 
      volunteer.name.toLowerCase().includes(lowercaseSearch) ||
      (volunteer.email && volunteer.email.toLowerCase().includes(lowercaseSearch)) ||
      (volunteer.phoneNumber && volunteer.phoneNumber.includes(lowercaseSearch)) ||
      (volunteer.commodity && volunteer.commodity.toLowerCase().includes(lowercaseSearch))
    );

    setFilteredVolunteers(filtered);
  }, [volunteers, volunteerSearchTerm]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditJob = (jobId: string) => {
    navigate(`/admin/jobs/edit/${jobId}`);
  };

  const handleDeleteClick = (jobId: string) => {
    setJobToDelete(jobId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteJob(jobToDelete);

      setJobs(jobs.filter(job => job.id !== jobToDelete));

    } catch (err) {
      console.error('Error deleting job:', err);
      setError('Dienst konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  const handleViewDetails = (jobId: string) => {
    navigate(`/admin/jobs/${jobId}`);
  };

  const handleViewVolunteerDetails = (volunteerId: string) => {
    navigate(`/admin/volunteers/${volunteerId}`);
  };

  const handleUpdateRegistrationStatus = async (registrationId: string, data: { isConfirmed: boolean, hasAttended: boolean }) => {
    try {
      setUpdatingRegistrationId(registrationId);
      await updateRegistration(registrationId, data);

      const updatedRegistrationsData = [...registrationsData];
      updatedRegistrationsData.forEach(jobData => {
        jobData.registrations = jobData.registrations.map(reg => {
          if (reg.id === registrationId) {
            return { ...reg, ...data };
          }
          return reg;
        });
      });
      setRegistrationsData(updatedRegistrationsData);

      const allRegistrations: Registration[] = [];
      updatedRegistrationsData.forEach(jobData => {
        jobData.registrations.forEach(reg => {
          allRegistrations.push({
            ...reg,
            jobName: jobData.job.name
          });
        });
      });

      let filtered = allRegistrations;
      if (statusFilter !== 'all') {
        if (statusFilter === 'confirmed') {
          filtered = filtered.filter(reg => reg.isConfirmed && !reg.hasAttended);
        } else if (statusFilter === 'pending') {
          filtered = filtered.filter(reg => !reg.isConfirmed);
        } else if (statusFilter === 'attended') {
          filtered = filtered.filter(reg => reg.hasAttended);
        }
      }

      if (searchTerm) {
        const lowercaseSearch = searchTerm.toLowerCase();
        filtered = filtered.filter(reg =>
          (reg.volunteerName?.toLowerCase().includes(lowercaseSearch)) ||
          (reg.jobName?.toLowerCase().includes(lowercaseSearch))
        );
      }

      setFilteredRegistrations(filtered);

    } catch (err) {
      console.error('Error updating registration status:', err);
      setError('Anmeldungsstatus konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setUpdatingRegistrationId(null);
    }
  };

  const handleEditRegistration = (registrationId: string) => {
    navigate(`/admin/registrations/edit/${registrationId}`);
  };

  const handleNewVolunteerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVolunteerForm({ ...newVolunteerForm, [name]: value });
    if (volunteerFormErrors[name]) {
      setVolunteerFormErrors({ ...volunteerFormErrors, [name]: '' });
    }
  };

  const handleCommodityChange = (e: SelectChangeEvent) => {
    setNewVolunteerForm({ ...newVolunteerForm, commodity: e.target.value });
  };  const validateVolunteerForm = () => {
    const errors: { [key: string]: string } = {};
    if (!newVolunteerForm.firstname) errors.firstname = 'Bitte geben Sie einen Vornamen ein';
    if (!newVolunteerForm.lastname) errors.lastname = 'Bitte geben Sie einen Nachnamen ein';
    
    // Require either email or phone number
    if (!newVolunteerForm.email && !newVolunteerForm.phoneNumber) {
      errors.email = 'Bitte geben Sie eine E-Mail-Adresse oder Telefonnummer ein';
      errors.phoneNumber = 'Bitte geben Sie eine Telefonnummer oder E-Mail-Adresse ein';
    } else if (newVolunteerForm.email && !/\S+@\S+\.\S+/.test(newVolunteerForm.email)) {
      errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    setVolunteerFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateVolunteer = async () => {
    if (!validateVolunteerForm()) return;

    try {
      setCreatingVolunteer(true);
      const response = await registerVolunteer({
        name: `${newVolunteerForm.firstname.trim()} ${newVolunteerForm.lastname.trim()}`.trim(),
        email: newVolunteerForm.email,
        phoneNumber: newVolunteerForm.phoneNumber || undefined,
        commodity: newVolunteerForm.commodity || undefined,
      });

      if (response.isNewRegistration && response.pin) {
        const updatedVolunteers = await getAllVolunteers();
        setVolunteers(updatedVolunteers);

        setNewVolunteerForm({
          firstname: '',
          lastname: '',
          email: '',
          phoneNumber: '',
          commodity: '',
        });
      }    } catch (error) {
      console.error('Error creating volunteer:', error);      setVolunteerFormErrors({
        submit: 'Helfer konnte nicht erstellt werden. Die E-Mail-Adresse oder Telefonnummer ist möglicherweise bereits registriert.'
      });
    } finally {
      setCreatingVolunteer(false);
    }
  };

  const handleNewVolunteerDialogClose = () => {
    setNewVolunteerDialogOpen(false);
    setNewVolunteerForm({
      firstname: '',
      lastname: '',
      email: '',
      phoneNumber: '',
      commodity: '',
    });
    setVolunteerFormErrors({});
  };

  const handleVolunteerSelect = (e: SelectChangeEvent) => {
    setSelectedVolunteer(e.target.value);
    if (registrationFormErrors.volunteer) {
      setRegistrationFormErrors({ ...registrationFormErrors, volunteer: '' });
    }
  };

  const handleJobSelect = (e: SelectChangeEvent) => {
    setSelectedJob(e.target.value);
    setSelectedTimeslot('');
    if (registrationFormErrors.job) {
      setRegistrationFormErrors({ ...registrationFormErrors, job: '' });
    }
    if (registrationFormErrors.timeslot) {
      setRegistrationFormErrors({ ...registrationFormErrors, timeslot: '' });
    }
  };

  const handleTimeslotSelect = (e: SelectChangeEvent) => {
    setSelectedTimeslot(e.target.value);
    if (registrationFormErrors.timeslot) {
      setRegistrationFormErrors({ ...registrationFormErrors, timeslot: '' });
    }
  };
  const validateRegistrationForm = () => {
    const errors: { [key: string]: string } = {};
    if (!selectedVolunteer) errors.volunteer = 'Bitte wählen Sie einen Helfer aus';
    if (!selectedJob) errors.job = 'Bitte wählen Sie einen Dienst aus';
    if (availableTimeslots.length > 0 && !selectedTimeslot) {
      errors.timeslot = 'Bitte wählen Sie ein Zeitfenster aus';
    }

    setRegistrationFormErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const handleExportVolunteersExcel = () => {
    const data = sortedFilteredVolunteers.map(vol => {
      const totalMinutes = getVolunteerRegisteredMinutes(vol.id);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return {
        Name: vol.name,
        Email: vol.email,
        Telefon: vol.phoneNumber || '',
        Organisation: vol.commodity || '',
        Registrierungen: getVolunteerRegistrationCount(vol.id),
        Zeit: `${hours > 0 ? hours + ' Std ' : ''}${minutes} Min`,
      };
    });
    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Helfer');
    // Export
    XLSX.writeFile(wb, 'helfer_export.xlsx');
  };

  const handleCreateRegistration = async () => {
    if (!validateRegistrationForm()) return;

    try {
      setCreatingRegistration(true);
      await createRegistration({
        volunteerId: selectedVolunteer,
        jobId: selectedJob,
        timeslotId: selectedTimeslot
      });

      const updatedData = await getAllRegistrations();
      setRegistrationsData(updatedData);

      const allRegistrations: Registration[] = [];
      updatedData.forEach(jobData => {
        jobData.registrations.forEach(reg => {
          allRegistrations.push({
            ...reg,
            jobName: jobData.job.name
          });
        });
      });
      setFilteredRegistrations(allRegistrations);

      setNewRegistrationDialogOpen(false);
      setSelectedVolunteer('');
      setSelectedJob('');
      setSelectedTimeslot('');

    } catch (error) {
      console.error('Error creating registration:', error);      setRegistrationFormErrors({
        submit: 'Anmeldung konnte nicht erstellt werden. Der Helfer ist möglicherweise bereits für diesen Dienst oder dieses Zeitfenster angemeldet.'
      });
    } finally {
      setCreatingRegistration(false);
    }
  };

  const handleNewRegistrationDialogClose = () => {
    setNewRegistrationDialogOpen(false);
    setSelectedVolunteer('');
    setSelectedJob('');
    setSelectedTimeslot('');
    setRegistrationFormErrors({});
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const renderJobCard = (job: Job) => {
    return (
      <Card key={job.id} sx={{ mb: 2, position: 'relative' }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" gutterBottom>
            {job.name}
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{job.location}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{job.requiredVolunteers} Helfer benötigt</Typography>
              </Box>
            </Grid>
          </Grid>
          
          {job.isActive ? (
            <Chip label="Aktiv" color="success" size="small" />
          ) : (
            <Chip label="Inaktiv" color="default" size="small" />
          )}
        </CardContent>
        
        <CardActions sx={{ pt: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button 
            startIcon={<VisibilityIcon />} 
            size="small" 
            onClick={() => handleViewDetails(job.id)}
          >
            Details
          </Button>
          <Button 
            startIcon={<EditIcon />} 
            size="small" 
            color="primary" 
            onClick={() => handleEditJob(job.id)}
          >
            Bearbeiten
          </Button>
          <Button 
            startIcon={<DeleteIcon />} 
            size="small" 
            color="error" 
            onClick={() => handleDeleteClick(job.id)}
          >
            Löschen
          </Button>
        </CardActions>
      </Card>
    );
  };

  // Function to render volunteer cards for mobile view
  const renderVolunteerCard = (volunteer: Volunteer) => {
    return (
      <Card key={volunteer.id} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" gutterBottom>
            {volunteer.name}
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {volunteer.email}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Organisation:</strong> {volunteer.commodity || 'Nicht angegeben'}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Zeit:</strong> {volunteer.totalMinutesWorked || 0} Min.
              </Typography>
            </Grid>
          </Grid>
          
        
        </CardContent>
        
        <CardActions sx={{ pt: 0 }}>
          <Button 
            startIcon={<VisibilityIcon />}
            size="small" 
            onClick={() => handleViewVolunteerDetails(volunteer.id)}
          >
            Details anzeigen
          </Button>
        </CardActions>
      </Card>
    );
  };

  // Function to render registration cards for mobile view
  const renderRegistrationCard = (registration: Registration) => {
    return (
      <Card key={registration.id} sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            {registration.volunteerName}
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            {registration.jobName}
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />                <Typography variant="body2">
                  {registration.timeslotStart ? format(new Date(registration.timeslotStart), 'dd.MM.yyyy, HH:mm') : 'Keine Zeit'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>              <Typography variant="body2" color="text.secondary">
                Angemeldet: {format(new Date(registration.registeredAt), 'dd.MM.yyyy')}
              </Typography>
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {registration.hasAttended ? (
              <Chip label="Anwesend" color="success" size="small" />
            ) : registration.isConfirmed ? (
              <Chip label="Bestätigt" color="primary" size="small" />
            ) : (
              <Chip label="Ausstehend" color="warning" size="small" />
            )}
            
            {registration.actualMinutesWorked && (
              <Chip label={`${registration.actualMinutesWorked} Min.`} size="small" />
            )}
          </Box>
        </CardContent>
        
        <CardActions sx={{ pt: 0, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {!registration.isConfirmed && (
              <Tooltip title="Bestätigen">
                <IconButton
                  color="primary"
                  size="small"
                  disabled={updatingRegistrationId === registration.id}
                  onClick={() => handleUpdateRegistrationStatus(registration.id, {
                    isConfirmed: true,
                    hasAttended: false
                  })}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {registration.isConfirmed && !registration.hasAttended && (
              <Tooltip title="Als anwesend markieren">
                <IconButton
                  color="success" 
                  size="small"
                  disabled={updatingRegistrationId === registration.id}
                  onClick={() => handleUpdateRegistrationStatus(registration.id, {
                    isConfirmed: true,
                    hasAttended: true
                  })}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          <Button
            size="small"
            disabled={updatingRegistrationId === registration.id}
            onClick={() => handleEditRegistration(registration.id)}
          >
            Bearbeiten
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg">      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Admin-Bereich
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/admin/dashboard')}
            startIcon={<VisibilityIcon />}
          >
            Job-Übersicht
          </Button>
        </Box>

        <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
          <Box sx={{ 
            borderBottom: 1,
            borderColor: 'divider',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            p: isSmall ? 0.5 : 0
          }}>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              aria-label="admin dashboard tabs"
              centered={!isMobile}
            >
              <Tab label={isMobile ? "Jobs" : "Dienste"} {...a11yProps(0)} />
              <Tab label="Helfer" {...a11yProps(1)} />
              <Tab label={isMobile ? "Anmeld." : "Anmeldungen"} {...a11yProps(2)} />
              <Tab label={isMobile ? "Einst." : "Einstellungen"} {...a11yProps(3)} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/jobs/create')}
              >
                {isMobile ? "Neu" : "Neuen Dienst erstellen"}
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" m={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                {isMobile ? (
                  // Mobile view - cards
                  <Box>
                    {jobs.length === 0 ? (
                      <Alert severity="info">
                        Keine Dienste gefunden. Erstellen Sie einen neuen Dienst, um zu beginnen.
                      </Alert>
                    ) : (
                      jobs.map((job) => renderJobCard(job))
                    )}
                  </Box>
                ) : (
                  // Desktop view - table
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 650 }} aria-label="jobs table">
                      <TableHead>                    
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Ort</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Helfer</TableCell>
                          <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              Keine Dienste gefunden. Erstellen Sie einen neuen Dienst, um zu beginnen.
                            </TableCell>
                          </TableRow>
                        ) : (
                          jobs.map((job) => (
                            <TableRow
                              key={job.id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row">
                                {job.name}
                              </TableCell>
                              <TableCell>{job.location}</TableCell>
                              <TableCell>
                                {job.isActive ? (
                                  <Chip label="Aktiv" color="success" size="small" />
                                ) : (
                                  <Chip label="Inaktiv" color="default" size="small" />
                                )}
                              </TableCell>
                              <TableCell>{job.requiredVolunteers}</TableCell>
                              <TableCell align="right">
                                <Tooltip title="Details anzeigen">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDetails(job.id)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Bearbeiten">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditJob(job.id)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Löschen">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(job.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </TabPanel>          <TabPanel value={tabValue} index={1}>
            <Box display="flex" justifyContent="flex-end" mb={2} gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setNewVolunteerDialogOpen(true)}
              >
                {isMobile ? "Helfer hinzufügen" : "Neuen Helfer hinzufügen"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportVolunteersExcel}
              >
                Exportiere Helfer als Excel
              </Button>
            </Box>


            {loading ? (
              <Box display="flex" justifyContent="center" m={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                {/* Search field for volunteers - useful on both mobile and desktop */}
                <TextField
                  label="Helfer suchen"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={volunteerSearchTerm}
                  placeholder="Name, E-Mail oder Organisation"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setVolunteerSearchTerm(e.target.value)}
                />
              
                {isMobile ? (
                  // Mobile view - cards
                  <Box>
                    {filteredVolunteers.length === 0 ? (
                      <Alert severity="info">
                        Keine Helfer gefunden.
                      </Alert>
                    ) : (
                      filteredVolunteers.map((volunteer) => renderVolunteerCard(volunteer))
                    )}
                  </Box>
                ) : (
                  // Desktop view - table
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 650 }} aria-label="volunteers table">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              Name
                              <IconButton
                                size="small"
                                onClick={() => setVolunteerSort((prev) => prev === 'name-asc' ? 'name-desc' : 'name-asc')}
                                sx={{ ml: 1 }}
                                aria-label="Sortiere nach Name"
                              >
                                {volunteerSort === 'name-asc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>E-Mail</TableCell>
                          <TableCell>Organisation</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              Registrierungen
                              <IconButton
                                size="small"
                                onClick={() => setVolunteerSort((prev) => prev === 'reg-asc' ? 'reg-desc' : 'reg-asc')}
                                sx={{ ml: 1 }}
                                aria-label="Sortiere nach Registrierungen"
                              >
                                {volunteerSort === 'reg-asc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              Zeit
                              <IconButton
                                size="small"
                                onClick={() => setVolunteerSort((prev) => prev === 'time-asc' ? 'time-desc' : 'time-asc')}
                                sx={{ ml: 1 }}
                                aria-label="Sortiere nach Zeit"
                              >
                                 {volunteerSort === 'time-asc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedFilteredVolunteers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              Keine Helfer gefunden.
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedFilteredVolunteers.map((volunteer) => (
                            <TableRow
                              key={volunteer.id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row">
                                {volunteer.name}
                              </TableCell>
                              <TableCell>{volunteer.email}</TableCell>
                              <TableCell>{volunteer.commodity || 'Nicht angegeben'}</TableCell>
                              <TableCell>{getVolunteerRegistrationCount(volunteer.id)}</TableCell>
                              <TableCell>
                                {(() => {
                                  const totalMinutes = getVolunteerRegisteredMinutes(volunteer.id);
                                  const hours = Math.floor(totalMinutes / 60);
                                  const minutes = totalMinutes % 60;
                                  return `${hours > 0 ? hours + ' Std ' : ''}${minutes} Min`;
                                })()}
                              </TableCell>
  
                              <TableCell align="right">
                                <Tooltip title="Details anzeigen">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewVolunteerDetails(volunteer.id)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box display="flex" justifyContent={isMobile ? "space-between" : "flex-end"} alignItems="center" mb={2}>
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFilterPanelOpen(!filterPanelOpen)}
                  size="small"
                >
                  Filter
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setNewRegistrationDialogOpen(true)}
              >
                {isMobile ? "Neu" : "Neue Anmeldung"}
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" m={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                {/* Collapsible filter panel for mobile view */}
                {isMobile ? (
                  <Collapse in={filterPanelOpen}>
                    <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Suchen"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                              labelId="status-filter-label"
                              id="status-filter"
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                              label="Status"
                            >
                              <MenuItem value="all">Alle</MenuItem>
                              <MenuItem value="pending">Ausstehend</MenuItem>
                              <MenuItem value="confirmed">Bestätigt</MenuItem>
                              <MenuItem value="attended">Anwesend</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="job-filter-label">Dienst</InputLabel>
                            <Select
                              labelId="job-filter-label"
                              id="job-filter"
                              value={jobFilter}
                              onChange={(e) => setJobFilter(e.target.value)}
                              label="Dienst"
                            >
                              <MenuItem value="all">Alle Dienste</MenuItem>
                              {jobs.map(job => (
                                <MenuItem key={job.id} value={job.id}>{job.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="day-filter-label">Tag</InputLabel>
                            <Select
                              labelId="day-filter-label"
                              id="day-filter"
                              value={dayFilter}
                              onChange={(e) => setDayFilter(e.target.value)}
                              label="Tag"
                            >
                              <MenuItem value="all">Alle Tage</MenuItem>
                              {availableDays.map(day => (
                                <MenuItem key={day} value={day}>{day}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Collapse>
                ) : (
                  // Desktop filters
                  <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Suchen"
                      variant="outlined"
                      size="small"
                      sx={{ flexGrow: 1 }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        id="status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="all">Alle</MenuItem>
                        <MenuItem value="pending">Ausstehend</MenuItem>
                        <MenuItem value="confirmed">Bestätigt</MenuItem>
                        <MenuItem value="attended">Anwesend</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="job-filter-label">Dienst</InputLabel>
                      <Select
                        labelId="job-filter-label"
                        id="job-filter"
                        value={jobFilter}
                        onChange={(e) => setJobFilter(e.target.value)}
                        label="Dienst"
                      >
                        <MenuItem value="all">Alle Dienste</MenuItem>
                        {jobs.map(job => (
                          <MenuItem key={job.id} value={job.id}>{job.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="day-filter-label">Tag</InputLabel>
                      <Select
                        labelId="day-filter-label"
                        id="day-filter"
                        value={dayFilter}
                        onChange={(e) => setDayFilter(e.target.value)}
                        label="Tag"
                      >
                        <MenuItem value="all">Alle Tage</MenuItem>
                        {availableDays.map(day => (
                          <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                
                {isMobile ? (
                  // Mobile view - cards
                  <Box>
                    {filteredRegistrations.length === 0 ? (
                      <Alert severity="info">
                        Keine Anmeldungen gefunden.
                      </Alert>
                    ) : (
                      filteredRegistrations.map((registration) => renderRegistrationCard(registration))
                    )}
                  </Box>
                ) : (
                  // Desktop view - table
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 650 }} aria-label="registrations table">
                      <TableHead>                        <TableRow>
                          <TableCell>Helfer</TableCell>
                          <TableCell>Dienst</TableCell>
                          <TableCell>Zeitfenster</TableCell>
                          <TableCell>Anmeldedatum</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Gearbeitete Zeit</TableCell>
                          <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRegistrations.length === 0 ? (
                          <TableRow>                            <TableCell colSpan={7} align="center">
                              Keine Anmeldungen gefunden.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRegistrations.map((registration) => (
                            <TableRow
                              key={registration.id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell>{registration.volunteerName}</TableCell>
                              <TableCell>{registration.jobName}</TableCell>
                              <TableCell>                              {registration.timeslotStart ?
                                format(new Date(registration.timeslotStart), 'dd.MM.yyyy, HH:mm') :
                                'Unbekannt'}
                              </TableCell>
                              <TableCell>
                                {format(new Date(registration.registeredAt), 'dd.MM.yyyy')}
                              </TableCell>
                              <TableCell>
                                {registration.hasAttended ? (                                <Chip label="Anwesend" color="success" size="small" />
                                ) : registration.isConfirmed ? (
                                  <Chip label="Bestätigt" color="primary" size="small" />
                                ) : (
                                  <Chip label="Ausstehend" color="warning" size="small" />
                                )}
                              </TableCell>
                              <TableCell>{registration.actualMinutesWorked || 'Nicht erfasst'}</TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  {!registration.isConfirmed && (
                                    <IconButton
                                      color="primary"
                                      disabled={updatingRegistrationId === registration.id}
                                      onClick={() => handleUpdateRegistrationStatus(registration.id, {
                                        isConfirmed: true,
                                        hasAttended: false
                                      })}
                                      title="Confirm"
                                      size="small"
                                    >
                                      <CheckCircleIcon />
                                    </IconButton>
                                  )}

                                  {registration.isConfirmed && !registration.hasAttended && (
                                    <IconButton
                                      color="success"
                                      disabled={updatingRegistrationId === registration.id}
                                      onClick={() => handleUpdateRegistrationStatus(registration.id, {
                                        isConfirmed: true,
                                        hasAttended: true
                                      })}
                                      title="Mark as Attended"
                                      size="small"
                                    >
                                      <CheckCircleIcon />
                                    </IconButton>
                                  )}

                                  <Button
                                    size="small"
                                    disabled={updatingRegistrationId === registration.id}
                                    onClick={() => handleEditRegistration(registration.id)}
                                    sx={{ ml: 1 }}
                                  >
                                    Details
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </TabPanel>          <TabPanel value={tabValue} index={3}>
            <Typography>
              Dies ist ein Platzhalter für Anwendungseinstellungen.
              In einer vollständigen Implementierung würde dies die Konfiguration von Goodie-Schwellenwerten, E-Mail-Vorlagen usw. ermöglichen.
            </Typography>
          </TabPanel>
        </Paper>
      </Box>      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        fullScreen={isSmall}
      >
        <DialogTitle id="delete-dialog-title">
          Dienst Löschen
          {isSmall && (
            <IconButton
              aria-label="close"
              onClick={handleDeleteCancel}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie diesen Dienst löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden und entfernt alle zugehörigen Zeitfenster.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading} variant={isSmall ? "outlined" : "text"}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant={isSmall ? "contained" : "text"}
            disabled={deleteLoading}
            autoFocus
            startIcon={<DeleteIcon />}
          >
            {deleteLoading ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>      <Dialog
        open={newVolunteerDialogOpen}
        onClose={handleNewVolunteerDialogClose}
        aria-labelledby="new-volunteer-dialog-title"
        maxWidth="sm"
        fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle id="new-volunteer-dialog-title" sx={{
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          Neuen Helfer anlegen
          {isSmall && (
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={handleNewVolunteerDialogClose} 
              aria-label="close"
              size="small"
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ mt: isSmall ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}>            <TextField
              required
              label="Vorname"
              name="firstname"
              value={newVolunteerForm.firstname}
              onChange={handleNewVolunteerChange}
              error={!!volunteerFormErrors.firstname}
              helperText={volunteerFormErrors.firstname}
              fullWidth
            />            <TextField
              label="E-Mail"
              name="email"
              type="email"
              value={newVolunteerForm.email}
              onChange={handleNewVolunteerChange}
              error={!!volunteerFormErrors.email}
              helperText={volunteerFormErrors.email || "E-Mail oder Telefonnummer erforderlich"}
              fullWidth
            />
            
            <TextField
              required
              label="Nachname"
              name="lastname"
              value={newVolunteerForm.lastname}
              onChange={handleNewVolunteerChange}
              error={!!volunteerFormErrors.lastname}
              helperText={volunteerFormErrors.lastname}
              fullWidth
            />

            <TextField
              label="Telefonnummer"
              name="phoneNumber"
              value={newVolunteerForm.phoneNumber}
              onChange={handleNewVolunteerChange}
              error={!!volunteerFormErrors.phoneNumber}
              helperText={volunteerFormErrors.phoneNumber || "Telefonnummer oder E-Mail erforderlich"}
              fullWidth
            /><FormControl fullWidth>
              <InputLabel id="commodity-label">Organisation</InputLabel>
              <Select
                labelId="commodity-label"
                name="commodity"
                value={newVolunteerForm.commodity}
                onChange={handleCommodityChange}
                label="Organisation"
              >
                <MenuItem value="">
                  <em>Keine</em>
                </MenuItem>
                <MenuItem value="Musikverein">Musikverein</MenuItem>
                <MenuItem value="Kameradschaftsbund">Kameradschaftsbund</MenuItem>
              </Select>
            </FormControl>

            {volunteerFormErrors.submit && (
              <Alert severity="error">{volunteerFormErrors.submit}</Alert>
            )}
          </Box>        </DialogContent>
        <DialogActions sx={{ 
          py: 2,
          display: 'flex',
          justifyContent: isSmall ? 'space-between' : 'flex-end',
          width: '100%'
        }}>
          <Button 
            onClick={handleNewVolunteerDialogClose} 
            disabled={creatingVolunteer}
            variant={isSmall ? "outlined" : "text"}
            fullWidth={isSmall}
            sx={{ mr: isSmall ? 1 : 0 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateVolunteer}
            color="primary"
            variant={isSmall ? "contained" : "text"}
            disabled={creatingVolunteer}
            fullWidth={isSmall}
            startIcon={<AddIcon />}
          >
            {creatingVolunteer ? 'Erstelle...' : 'Helfer anlegen'}
          </Button>
        </DialogActions>
      </Dialog>      <Dialog
        open={newRegistrationDialogOpen}
        onClose={handleNewRegistrationDialogClose}
        aria-labelledby="new-registration-dialog-title"
        maxWidth="sm"
        fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle id="new-registration-dialog-title" sx={{
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          Neue Anmeldung
          {isSmall && (
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={handleNewRegistrationDialogClose} 
              aria-label="close"
              size="small"
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ mt: isSmall ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth error={!!registrationFormErrors.volunteer}>              <InputLabel id="volunteer-select-label">Helfer</InputLabel>
              <Select
                labelId="volunteer-select-label"
                value={selectedVolunteer}
                onChange={handleVolunteerSelect}
                label="Helfer"
              >
                <MenuItem value="">
                  <em>Helfer auswählen</em>
                </MenuItem>
                {volunteers.map((volunteer) => (
                  <MenuItem key={volunteer.id} value={volunteer.id}>
                    {volunteer.name} ({volunteer.email})
                  </MenuItem>
                ))}
              </Select>
              {registrationFormErrors.volunteer && (
                <Typography color="error" variant="caption">
                  {registrationFormErrors.volunteer}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth error={!!registrationFormErrors.job}>              <InputLabel id="job-select-label">Dienst</InputLabel>
              <Select
                labelId="job-select-label"
                value={selectedJob}
                onChange={handleJobSelect}
                label="Dienst"
              >
                <MenuItem value="">
                  <em>Dienst auswählen</em>
                </MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.name} - {job.location}
                  </MenuItem>
                ))}
              </Select>
              {registrationFormErrors.job && (
                <Typography color="error" variant="caption">
                  {registrationFormErrors.job}
                </Typography>
              )}
            </FormControl>

            {loadingTimeslots ? (
              <Box display="flex" justifyContent="center" my={2}>                <CircularProgress size={24} />
                <Typography ml={1} variant="body2" color="text.secondary">
                  Zeitfenster werden geladen...
                </Typography>
              </Box>
            ) : timeslotError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {timeslotError}
              </Alert>
            ) : availableTimeslots.length > 0 ? (
              <FormControl fullWidth error={!!registrationFormErrors.timeslot}>                <InputLabel id="timeslot-select-label">Zeitfenster</InputLabel>
                <Select
                  labelId="timeslot-select-label"
                  value={selectedTimeslot}
                  onChange={handleTimeslotSelect}
                  label="Zeitfenster"
                >
                  <MenuItem value="">
                    <em>Zeitfenster auswählen</em>
                  </MenuItem>
                  {availableTimeslots.map((timeslot) => {
                    const isFull = timeslot.spotsLeft <= 0;
                    const isDisabled = isFull || timeslot.isVolunteerAlreadyRegistered || timeslot.hasTimeConflict;

                    return (
                      <MenuItem
                        key={timeslot.id}
                        value={timeslot.id}
                        disabled={isDisabled}
                        sx={{
                          color: isDisabled ? 'text.disabled' : 'text.primary',
                          '& .MuiListItemText-secondary': {
                            color: isFull ? 'error.main' :
                              timeslot.spotsLeft <= 2 ? 'warning.main' : 'success.main'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>                          <Typography variant="body1">
                            {format(new Date(timeslot.startTime), 'dd.MM.yyyy, HH:mm')} -
                            {format(new Date(timeslot.endTime), 'HH:mm')}
                          </Typography><Typography variant="caption" color={
                            timeslot.hasTimeConflict ? "error" :
                              timeslot.isVolunteerAlreadyRegistered ? "error" :
                                isFull ? "error" :
                                  timeslot.spotsLeft <= 2 ? "warning.main" : "success.main"
                          }>
                            {timeslot.hasTimeConflict ? 'Zeitkonflikt mit anderen Verpflichtungen' :
                              timeslot.isVolunteerAlreadyRegistered ? 'Bereits angemeldet' :
                                isFull ? 'VOLL' :
                                  `${timeslot.spotsLeft} Platz${timeslot.spotsLeft !== 1 ? 'e' : ''} frei`}
                            ({timeslot.registeredVolunteers}/{timeslot.requiredVolunteers} Helfer)
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
                {registrationFormErrors.timeslot && (
                  <Typography color="error" variant="caption">
                    {registrationFormErrors.timeslot}
                  </Typography>
                )}                {selectedVolunteer && volunteerExistingTimeslots.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Der Helfer hat bereits {volunteerExistingTimeslots.length} bestehende Verpflichtung{volunteerExistingTimeslots.length !== 1 ? 'en' : ''}. Überschneidende Zeitfenster sind deaktiviert.
                  </Typography>
                )}
              </FormControl>
            ) : selectedJob ? (
              <Typography color="text.secondary" mt={1} mb={2}>
                Keine Zeitfenster für diesen Job verfügbar. Die Anmeldung wird ohne spezifisches Zeitfenster erstellt.
              </Typography>
            ) : null}

            {registrationFormErrors.submit && (
              <Alert severity="error">{registrationFormErrors.submit}</Alert>
            )}
          </Box>        </DialogContent>
        <DialogActions sx={{ 
          py: 2,
          display: 'flex',
          justifyContent: isSmall ? 'space-between' : 'flex-end',
          width: '100%'
        }}>
          <Button 
            onClick={handleNewRegistrationDialogClose} 
            disabled={creatingRegistration}
            variant={isSmall ? "outlined" : "text"}
            fullWidth={isSmall}
            sx={{ mr: isSmall ? 1 : 0 }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateRegistration}
            color="primary"
            variant={isSmall ? "contained" : "text"}
            disabled={creatingRegistration}
            fullWidth={isSmall}
            startIcon={<AddIcon />}
          >
            {creatingRegistration ? 'Erstelle...' : 'Anmeldung erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage;

